import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  User as FirebaseUser,
  updateProfile
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import { db, auth } from '../firebase/config';

export interface Teacher {
  id: string;
  email: string;
  name: string;
  subjects: string[];
  grades: string[];
  school?: string;
  avatar?: string;
  isAdmin?: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface AuthState {
  user: Teacher | null;
  loading: boolean;
  error: string | null;
}

class AuthService {
  private currentUser: Teacher | null = null;

  // Sign in with email and password
  async signIn(email: string, password: string): Promise<Teacher> {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;
      
      // Get teacher data from Firestore
      const teacherData = await this.getTeacherData(firebaseUser.uid);
      
      if (!teacherData) {
        throw new Error('Teacher profile not found');
      }
      
      this.currentUser = teacherData;
      return teacherData;
    } catch (error: any) {
      console.error('Sign in error:', error);
      throw new Error(error.message || 'Failed to sign in');
    }
  }

  // Sign up new teacher
  async signUp(email: string, password: string, teacherData: Omit<Teacher, 'id' | 'createdAt' | 'updatedAt'>): Promise<Teacher> {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;
      
      // Create teacher profile in Firestore
      const teacher: Teacher = {
        id: firebaseUser.uid,
        ...teacherData,
        createdAt: new Date(),
        updatedAt: new Date()
      };
      
      await this.saveTeacherData(teacher);
      
      // Update Firebase user profile
      await updateProfile(firebaseUser, {
        displayName: teacherData.name
      });
      
      this.currentUser = teacher;
      return teacher;
    } catch (error: any) {
      console.error('Sign up error:', error);
      throw new Error(error.message || 'Failed to sign up');
    }
  }

  // Sign out
  async signOut(): Promise<void> {
    try {
      await signOut(auth);
      this.currentUser = null;
    } catch (error: any) {
      console.error('Sign out error:', error);
      throw new Error(error.message || 'Failed to sign out');
    }
  }

  // Get current user
  getCurrentUser(): Teacher | null {
    return this.currentUser;
  }

  // Get teacher data from Firestore
  private async getTeacherData(teacherId: string): Promise<Teacher | null> {
    try {
      const teacherDoc = await getDoc(doc(db, 'teachers', teacherId));
      
      if (teacherDoc.exists()) {
        const data = teacherDoc.data();
        return {
          id: teacherDoc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date()
        } as Teacher;
      }
      
      return null;
    } catch (error) {
      console.error('Error getting teacher data:', error);
      return null;
    }
  }

  // Save teacher data to Firestore
  private async saveTeacherData(teacher: Teacher): Promise<void> {
    try {
      const teacherData: any = {
        email: teacher.email,
        name: teacher.name,
        subjects: teacher.subjects,
        grades: teacher.grades,
        createdAt: teacher.createdAt,
        updatedAt: teacher.updatedAt
      };

      // Only add optional fields if they have values
      if (teacher.school) teacherData.school = teacher.school;
      if (teacher.avatar) teacherData.avatar = teacher.avatar;

      await setDoc(doc(db, 'teachers', teacher.id), teacherData);
    } catch (error) {
      console.error('Error saving teacher data:', error);
      throw error;
    }
  }

  // Update teacher profile
  async updateTeacherProfile(updates: Partial<Teacher>): Promise<void> {
    if (!this.currentUser) {
      throw new Error('No authenticated user');
    }

    try {
      const updatedTeacher = {
        ...this.currentUser,
        ...updates,
        updatedAt: new Date()
      };

      // Filter out undefined values before updating
      const updateData: any = {
        updatedAt: new Date()
      };

      // Only include fields that are not undefined
      Object.entries(updates).forEach(([key, value]) => {
        if (value !== undefined) {
          updateData[key] = value;
        }
      });

      await updateDoc(doc(db, 'teachers', this.currentUser.id), updateData);

      this.currentUser = updatedTeacher;
    } catch (error: any) {
      console.error('Error updating teacher profile:', error);
      throw new Error(error.message || 'Failed to update profile');
    }
  }

  // Listen to auth state changes
  onAuthStateChanged(callback: (user: Teacher | null) => void): () => void {
    return onAuthStateChanged(auth, async (firebaseUser: FirebaseUser | null) => {
      if (firebaseUser) {
        const teacherData = await this.getTeacherData(firebaseUser.uid);
        this.currentUser = teacherData;
        callback(teacherData);
      } else {
        this.currentUser = null;
        callback(null);
      }
    });
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    return this.currentUser !== null;
  }

  // Get current teacher ID
  getCurrentTeacherId(): string | null {
    return this.currentUser?.id || null;
  }
}

export const authService = new AuthService();
